// src/hooks/useMapOverlayPosition.ts
import { useLayoutEffect, RefObject } from 'react';
import { useMap } from '@/providers/MapProvider';
import { Coordinate } from 'ol/coordinate';

interface UseMapOverlayPositionProps {
  position: Coordinate | null;
  elementRef: RefObject<HTMLElement | null>;
  isOpen?: boolean;
  offset?: [number, number]; // e.g., [0, -12] for vertical offset
}

export const useMapOverlayPosition = ({
  position,
  elementRef,
  isOpen = true,
  offset = [0, -12],
}: UseMapOverlayPositionProps) => {
  const { map } = useMap();

  useLayoutEffect(() => {
    const element = elementRef.current;

    if (!map || !element || !isOpen || !position) {
      if (element) element.style.display = 'none';
      return;
    }

    const updatePosition = () => {
      const pixel = map.getPixelFromCoordinate(position);
      if (!pixel) {
        element.style.display = 'none';
        return;
      }

      const [x, y] = pixel;
      const [offsetX, offsetY] = offset;
      const mapSize = map.getSize();
      if (!mapSize) return;

      const rect = element.getBoundingClientRect();
      const popupWidth = rect.width;
      const popupHeight = rect.height;
      const [mapWidth, mapHeight] = mapSize;

      const PADDING = 12;

      // Vertical: flip below if too close to top
      let anchorY = '-100%';
      let finalOffsetY = offsetY;
      if (y - popupHeight - Math.abs(offsetY) < PADDING) {
        anchorY = '0%';
        finalOffsetY = Math.abs(offsetY);
      }

      // Horizontal: shift if too close to edges
      let anchorX = '-50%';
      let finalOffsetX = offsetX;
      if (x - popupWidth / 2 < PADDING) {
        anchorX = '0%';
        finalOffsetX = PADDING;
      } else if (x + popupWidth / 2 > mapWidth - PADDING) {
        anchorX = '-100%';
        finalOffsetX = -PADDING;
      }

      element.style.transform = `translate(${x}px, ${y}px) translate(${anchorX}, ${anchorY}) translate(${finalOffsetX}px, ${finalOffsetY}px)`;
      element.style.display = 'block';
    };

    updatePosition();

    map.on('postrender', updatePosition);

    return () => {
      map.un('postrender', updatePosition);
    };
  }, [map, position, isOpen, elementRef]); // Dependencies are stable now
};
